import type { SocialLoginProvider } from '~kui/types/auth';

import { useMutation } from '@urql/vue';

import type { EntryCta, EntrySource } from '~/graphql/generated/graphql';

import { initiateSocialLoginMutation } from '~/graphql/documents/core';
import { SocialAuthProvider } from '~/graphql/generated/graphql';
import { parseSelectedEntityFromQuery } from '~/utils/unified/selectedEntity';
import { readUtmKeyFromQuery } from '~/utils/unified/signupLink';

const PROVIDER_MAP: Record<SocialLoginProvider, SocialAuthProvider> = {
  google: SocialAuthProvider.Google,
  microsoft: SocialAuthProvider.Microsoft,
};

const TRUSTED_REDIRECT_ORIGINS = ['https://accounts.google.com', 'https://login.microsoftonline.com'];

const parseRedirectUrl = (redirectUrl: string): URL | null => {
  try {
    return new URL(redirectUrl);
  }
  catch {
    return null;
  }
};

interface InitiateSocialLoginContext {
  entrySource?: EntrySource | null;
  entryCta?: EntryCta | null;
}

export const useInitiateSocialLogin = () => {
  const route = useRoute();
  const { $logErrorMessage } = useNuxtApp();
  const { executeMutation } = useMutation(initiateSocialLoginMutation);

  /**
   * Kicks off the OAuth redirect for a social provider, returning to the current page
   * (route.fullPath) after the exchange. Returns false when initiation failed — the
   * caller surfaces its own error and releases loading. On success the browser is
   * already hard-navigating to the provider, so callers should keep loading on.
   */
  const initiateSocialLogin = async (
    provider: SocialLoginProvider,
    { entrySource = null, entryCta = null }: InitiateSocialLoginContext = {},
  ): Promise<boolean> => {
    const selectedEntity = parseSelectedEntityFromQuery(route.query);
    const utmKey = readUtmKeyFromQuery(route.query);

    const result = await executeMutation({
      input: {
        provider: PROVIDER_MAP[provider],
        returnUrl: route.fullPath,
        entrySource,
        entryCta,
        authStartTime: String(Date.now()),
        ...(selectedEntity ? { selectedEntity } : {}),
        ...(utmKey ? { utmKey } : {}),
      },
    });

    const redirectUrl = result.data?.core.auth.initiateSocialLogin.redirectUrl;
    const parsedRedirect = redirectUrl ? parseRedirectUrl(redirectUrl) : null;
    const trustedRedirect = parsedRedirect && TRUSTED_REDIRECT_ORIGINS.includes(parsedRedirect.origin)
      ? parsedRedirect
      : null;
    if (result.error || !trustedRedirect) {
      if (redirectUrl && !trustedRedirect) {
        // A rejected URL means the backend redirects somewhere TRUSTED_REDIRECT_ORIGINS doesn't
        // cover (new provider, changed endpoint) — the allowlist needs updating alongside it.
        // Only the origin is logged: the full URL's query may carry user data (e.g. login_hint).
        $logErrorMessage('[useInitiateSocialLogin] redirect URL rejected by provider allowlist', {
          rejectedOrigin: parsedRedirect?.origin ?? 'unparseable',
        });
      }
      return false;
    }

    window.location.href = trustedRedirect.href;
    return true;
  };

  return { initiateSocialLogin };
};
